import {
	useEffect,
	useState,
	useRef,
	useContext,
} from 'react';
import { useDispatch } from 'react-redux';
import { isEqual } from 'underscore';
import { DeviceTypes, GreenroomState as GRS } from '../stores/types/types';
import { RESTRICTED_USER_STATES } from '../stores/reducers/session';
import { ReportError } from '../connection/errors';
import * as VideoActions from '../stores/actions/video';
import * as SessionActions from '../stores/actions/session';
import * as VideoStreams from '../utils/video/video-streams';
import { hideDeviceAlert } from '../components/general-components/alert-service';
import store from '../stores/main';
import { OpenTokContext } from '../contexts/opentok/opentok-context';
import useUpdateDevices from './devices/use-update-devices';

export interface mediaErrorMessage {
	message: string;
	description: string;
}


const useMediaDevices = () => {
	const dispatch = useDispatch();

	const { opentok, changeAudioDevice, changeVideoDevice } = useContext(OpenTokContext);

	const { updateMediaDevices } = useUpdateDevices();

	const [error, setError] = useState<mediaErrorMessage>({ message: '', description: '' });

	const deviceRef = useRef({
		cameras: [] as MediaDeviceInfo[],
		microphones: [] as MediaDeviceInfo[],
		speakers: [] as MediaDeviceInfo[],
	});

	const prevDeviceRef = useRef({
		previousSelectedCameraId: null as string | null,
		previousSelectedMicrophoneId: null as string | null,
		previousSelectedSpeakersId: null as string | null,
	});

	const getError = (device: number, permission: boolean) => {
		dispatch(SessionActions.setRestrictedUserState(device));

		let deviceString: string = '';
		if (device === RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE) {
			deviceString = 'camera and microphone';
		} else if (device === RESTRICTED_USER_STATES.CAMERA) {
			deviceString = 'camera';
		} else if (device === RESTRICTED_USER_STATES.MICROPHONE) {
			deviceString = 'microphone';
		}

		const descriptionString = permission
			? `Your browser is preventing access to your ${deviceString}. 
			[Learn how to allow access in your browser.](https://help.brand.live/hc/en-us/articles/4406217830295-Camera-and-Microphone-Access-Required-)`
			: `Greenroom was unable to detect a ${deviceString}. Please check your hardware and try again.`;

		setError({
			message: `${deviceString} access required`,
			description: descriptionString,
		});

		return device;
	};

	const updateMediaStream = async (
		newCam: MediaDeviceInfo | null,
		newMic: MediaDeviceInfo | null,
		selectedVideo: MediaDeviceInfo | null,
		selectedAudio: MediaDeviceInfo | null,
		mainVideoStream: MediaStream | null,
		restrictedUserState: number,
	) => {
		// ensure camera or mic can produce a live feed
		const [hasCam, hasMic] = await VideoStreams.getLiveDevices(
			newCam?.deviceId,
			newMic?.deviceId,
		);

		if (hasCam || hasMic) {
			const restrictedDeviceStates = [
				RESTRICTED_USER_STATES.CAMERA,
				RESTRICTED_USER_STATES.MICROPHONE,
				RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE,
			];

			if (!hasCam && restrictedUserState !== RESTRICTED_USER_STATES.CAMERA) {
				getError(RESTRICTED_USER_STATES.CAMERA, false);
			}

			if (!hasMic && restrictedUserState !== RESTRICTED_USER_STATES.MICROPHONE) {
				getError(RESTRICTED_USER_STATES.MICROPHONE, false);
			}

			if (hasCam && hasMic && restrictedDeviceStates.includes(restrictedUserState)) {
				dispatch(SessionActions.setRestrictedUserState(RESTRICTED_USER_STATES.NONE));
				hideDeviceAlert();
			}

			if (!opentok?.session.isConnected()) {
				const str = await VideoStreams.getCameraStream(newCam?.deviceId, newMic?.deviceId);
				dispatch(VideoActions.setMainVideoStream(str));
			}
		} else if (restrictedUserState !== RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE) {
			getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, false);
		}
	};

	// initializes hardware devices and fires if device permissions change
	const checkMediaDevices = async (forceEnabled = false) => {
		// get current redux state as this may fire from an permissions event
		const {
			VideoReducer: {
				videoDevices: videoDevicesRedux,
				audioDevices: audioDevicesRedux,
				audioOutputs: audioOutputsRedux,
				selectedVideo: selectedVideoRedux,
				selectedAudio: selectedAudioRedux,
				selectedOutput: selectedOutputRedux,
				selectedResolution: selectedResolutionRedux,
				availableResolutions: availableResolutionsRedux,
				mainVideoStream: mainVideoStreamRedux,
				isInitialDeviceLoad: isInitialDeviceLoadRedux,
			},
			SessionReducer: {
				token: tokenRedux,
				restrictedUserState: restrictedUserStateRedux,
			},
		} = store.getState() as GRS;

		try {
			let cameraPermission = await VideoStreams.getCameraPermissionState(forceEnabled);
			let micPermission = await VideoStreams.getMicPermissionState(forceEnabled);

			if (cameraPermission === 'off' || micPermission === 'off') {
				return;
			}

			// if we reset permissions, we need to create a new stream for opentok
			if (!isInitialDeviceLoadRedux && (cameraPermission === 'prompt' || micPermission === 'prompt')) {
				dispatch(VideoActions.destroyMainVideoStream());
			}

			//! this is what is causing the green dot in the home view, before you get to a meeting
			// when JS requests getUserMedia it is telling the browser to turn on the camera and mic
			const mediaSupported = !!navigator.mediaDevices.getUserMedia;
			let tempStream = null;
			const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

			if (mediaSupported) {
				let currentRestrictedState = restrictedUserStateRedux;
				if ((window as any).chrome) {
					// if we don't have permissions, request them first, then
					// when we're done getting all the streams, delete the temp stream.
					if (cameraPermission !== 'granted' || micPermission !== 'granted') {
						try {
							tempStream = await VideoStreams.getDefaultCameraStream();
						} catch (e) {
							cameraPermission = await VideoStreams.getCameraPermissionState();
							micPermission = await VideoStreams.getMicPermissionState();
						}
					}

					if (cameraPermission === 'denied' && micPermission === 'denied') {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, true);
					} else if (cameraPermission === 'denied') {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA, true);
					} else if (micPermission === 'denied') {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.MICROPHONE, true);
					}
				} else if (isFirefox) {
					const allDevices = async () => {
						const devices = await navigator.mediaDevices.enumerateDevices();
						return devices;
					};
					await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

					const devices = await allDevices();

					const noPermissions: any = {};
					devices.forEach((mediaDevice) => {
						noPermissions[mediaDevice.kind] = mediaDevice.label === '';
					});

					if (noPermissions?.videoinput && noPermissions?.audioinput) {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, true);
					} else if (noPermissions?.videoinput) {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA, true);
					} else if (noPermissions?.audioinput) {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.MICROPHONE, true);
					}
				} else {
					try {
						tempStream = await VideoStreams.getDefaultCameraStream();
					} catch (e) {
						currentRestrictedState = getError(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE, true);
					}
				}

				if (tempStream) {
					VideoStreams.deleteStream(tempStream);
				}

				const {
					cameras,
					microphones,
					speakers,
				} = await updateMediaDevices(
					videoDevicesRedux,
					audioDevicesRedux,
					audioOutputsRedux,
				);

				deviceRef.current = { cameras, microphones, speakers };

				const oldCam = localStorage.getItem('camera');
				const oldCamUnsafe = cameras.find((c) => c.deviceId === oldCam);
				const cameraToUse = oldCamUnsafe ?? cameras[0];
				if (cameraToUse && !selectedVideoRedux) {
					dispatch(VideoActions.setSelectedVideo(cameraToUse));
				}

				const oldMic = localStorage.getItem('microphone');
				const oldMicUnsafe = microphones.find((m) => m.deviceId === oldMic);
				const micToUse = oldMicUnsafe ?? microphones[0];
				if (micToUse && !selectedAudioRedux) {
					dispatch(VideoActions.setSelectedAudio(micToUse));
				}

				const oldOutput = localStorage.getItem('speakers');
				const oldOutputUnsafe = speakers.find((s) => s.deviceId === oldOutput);
				const outputToUse = oldOutputUnsafe ?? speakers[0];
				if (outputToUse && !selectedOutputRedux) {
					dispatch(VideoActions.setSelectedAudioOutput(outputToUse));
				}

				if (availableResolutionsRedux[0] && !selectedResolutionRedux) {
					dispatch(VideoActions.setSelectedResolution(availableResolutionsRedux[0]));
				}

				await updateMediaStream(
					cameraToUse,
					micToUse,
					selectedVideoRedux,
					selectedAudioRedux,
					mainVideoStreamRedux,
					currentRestrictedState,
				);
			}
			return;
		} catch (e) {
			console.error(e);
			let message = 'Unknown Error';
			if (e instanceof Error) {
				message = e.message;
				ReportError(tokenRedux || '', window.navigator.userAgent, e.stack, message).catch((e2) => {
					console.error(e2);
				});
			}

			dispatch(SessionActions.setRestrictedUserState(RESTRICTED_USER_STATES.CAMERA_AND_MICROPHONE));
			if (e instanceof Error) {
				setError({
					message: 'camera and microphone access required',
					description: `${e.message}.`,
				});
			}
			return;
		} finally {
			dispatch(VideoActions.setIsInitialDeviceLoad(false));
		}
	};

	function findNewDevice(
		newDevices: MediaDeviceInfo[],
		oldDevices: MediaDeviceInfo[],
	): MediaDeviceInfo | undefined {
		// select non-default version of device that was just added
		// (keeps from "bugs" where system changes default without
		// firing devicechange event)
		const currIds = oldDevices.map((device) => device.deviceId).filter((id) => id !== 'default');
		const newIds = newDevices.map((device) => device.deviceId).filter((id) => id !== 'default');
		const addedDeviceId = newIds.find((id) => !currIds.includes(id));
		const addedDevice = newDevices.find((d) => d.deviceId === addedDeviceId);
		return addedDevice ?? newDevices[0] as MediaDeviceInfo | undefined;
	}

	const getNewSelectedDevice = (
		type: DeviceTypes,
		devices: MediaDeviceInfo[],
		oldDevices: MediaDeviceInfo[],
		currentSelectedDevice: MediaDeviceInfo | null,
	) => {
		// find if a new device was added
		const oldDeviceIds = oldDevices.map((d) => d.deviceId);
		const newDevices = devices.filter((d) => !oldDeviceIds.includes(d.deviceId));

		if (newDevices.length) {
			return findNewDevice(newDevices, oldDevices) ?? null;
		}

		const oldDeviceId = localStorage.getItem(type.toLowerCase());
		const oldDevice = devices.find((d) => d.deviceId === oldDeviceId);
		const previousDevice = devices.find(
			(d) => d.deviceId === prevDeviceRef.current[`previousSelected${type}Id`],
		);

		// go back to previous camera if it still exists again
		if (previousDevice) {
			prevDeviceRef.current[`previousSelected${type}Id`] = null;
			return previousDevice;
		}

		if (!oldDevice || oldDevice.deviceId === 'default') {
			prevDeviceRef.current[`previousSelected${type}Id`] = oldDeviceId;
			return findNewDevice(devices, oldDevices) ?? null;
		}

		// if the last selected device from local storage is found again
		if (oldDevice && !currentSelectedDevice) {
			prevDeviceRef.current[`previousSelected${type}Id`] = oldDeviceId;
			return oldDevice;
		}

		return currentSelectedDevice;
	};

	// fires when a device is plugged or unplugged
	const onDeviceChange = async () => {
		// get current redux state as this is fired from an event listener
		const {
			VideoReducer: {
				videoDevices: videoDevicesRedux,
				audioDevices: audioDevicesRedux,
				audioOutputs: audioOutputsRedux,
				selectedVideo: selectedVideoRedux,
				selectedAudio: selectedAudioRedux,
				selectedOutput: selectedOutputRedux,
				mainVideoStream: mainVideoStreamRedux,
				isInitialDeviceLoad: isInitialDeviceLoadRedux,
			},
			SessionReducer: {
				restrictedUserState: restrictedUserStateRedux,
			},
		} = store.getState() as GRS;

		// do not fire until checkMediaDevices has initialized the devices
		if (isInitialDeviceLoadRedux) {
			return;
		}

		const {
			cameras: oldCams,
			microphones: oldMics,
			speakers: oldSpeakers,
		} = deviceRef.current;

		const {
			cameras,
			microphones,
			speakers,
		} = await updateMediaDevices(
			videoDevicesRedux,
			audioDevicesRedux,
			audioOutputsRedux,
		);

		// devicechange will fire several times for a device that can be used for multiple inputs
		if (isEqual(deviceRef.current, { cameras, microphones, speakers })) {
			return;
		}

		const newSelectedCam = getNewSelectedDevice(
			DeviceTypes.Camera,
			cameras,
			oldCams,
			selectedVideoRedux,
		);
		if (
			newSelectedCam
			&& !isEqual(JSON.stringify(newSelectedCam), JSON.stringify(selectedVideoRedux))
		) {
			changeVideoDevice(newSelectedCam);
		}

		const newSelectedMic = getNewSelectedDevice(
			DeviceTypes.Microphone,
			microphones,
			oldMics,
			selectedAudioRedux,
		);
		if (
			newSelectedMic
			&& !isEqual(JSON.stringify(newSelectedMic), JSON.stringify(selectedAudioRedux))
		) {
			changeAudioDevice(newSelectedMic);
		}

		const newSelectedSpeakers = getNewSelectedDevice(
			DeviceTypes.Speakers,
			speakers,
			oldSpeakers,
			selectedOutputRedux,
		);

		if (
			newSelectedSpeakers
			&& !isEqual(JSON.stringify(newSelectedSpeakers), JSON.stringify(selectedOutputRedux))
		) {
			dispatch(VideoActions.setSelectedAudioOutput(newSelectedSpeakers));
		}

		deviceRef.current = { cameras, microphones, speakers };

		await updateMediaStream(
			newSelectedCam,
			newSelectedMic,
			selectedVideoRedux,
			selectedAudioRedux,
			mainVideoStreamRedux,
			restrictedUserStateRedux,
		);
	};

	const updateDeviceRef = async () => {
		deviceRef.current = await updateMediaDevices();
	}

	const permissionListener = async (name: string) => {
		if (!navigator.permissions) return; // for tests
		// @ts-ignore - camera and microphone name prop supported on chrome but not firefox
		const perm = await navigator.permissions.query( { name } );
		perm.onchange = () => {updateDeviceRef()}
	}

	useEffect(() => {
		updateDeviceRef();
		permissionListener('camera');
		permissionListener('microphone');
	}, []);

	return {
		checkMediaDevices,
		onDeviceChange,
		error,
	};
};

export default useMediaDevices;
